import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table,
} from "@themesberg/react-bootstrap";
import { useCookies } from "react-cookie";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createRewardCalcJob,
  getRewardCalcJobs,
  getRewardCalcSetting,
  retryRewardCalcJob,
  updateRewardCalcSetting,
} from "../../controller/api/rewardCalcJob";
import Pagination from "../../components/Pagination";
import { parseQuerySimple } from "../../helper/urlHelper";

const JOB_REFRESH_INTERVAL_MS = 10000;
const JOBS_PER_PAGE = 10;

const JOB_STATUS = {
  FAILED: -1,
  PENDING: 0,
  RUNNING: 1,
  DONE: 2,
};

const JOB_STATUS_LABELS = {
  [JOB_STATUS.FAILED]: { text: "Lỗi", variant: "danger" },
  [JOB_STATUS.PENDING]: { text: "Chờ xử lý", variant: "warning" },
  [JOB_STATUS.RUNNING]: { text: "Đang chạy", variant: "info" },
  [JOB_STATUS.DONE]: { text: "Hoàn thành", variant: "success" },
};

const GAME_OPTIONS = [
  { id: "keno", name: "Keno" },
  { id: "mega", name: "Mega 6/45" },
  { id: "power", name: "Power 6/55" },
  { id: "max3d", name: "Max3D" },
  { id: "max3dpro", name: "Max3D Pro" },
  { id: "lotto535", name: "Lotto 5/35" },
  { id: "classic", name: "Kiến thiết" },
  { id: "loto", name: "Loto" },
];

const CLASSIC_FAMILY_GAME_IDS = ["classic", "loto"];

const DEFAULT_AUTO_GAMES = {
  keno: false,
  mega: false,
  power: false,
  max3d: false,
  max3dpro: false,
  lotto535: false,
  classic: false,
  loto: false,
};

const DEFAULT_AUTO_PAY_GAMES = {
  keno: false,
  mega: false,
  power: false,
  max3d: false,
  max3dpro: false,
  lotto535: false,
  classic: false,
  loto: false,
};

export default () => {
  const [cookies] = useCookies(["lottmekong"]);
  const token = cookies.lottmekong;
  const history = useHistory();
  const location = useLocation();
  const initialQuery = parseQuerySimple(location.search);
  const initialGameId = GAME_OPTIONS.some(
    (game) => game.id === initialQuery.gameId
  )
    ? initialQuery.gameId
    : GAME_OPTIONS[0].id;
  const initialPeriod = initialQuery.period || "";

  const [gameId, setGameId] = useState(initialGameId);
  const [period, setPeriod] = useState(initialPeriod);
  const [periodInput, setPeriodInput] = useState(initialPeriod);
  const [autoGames, setAutoGames] = useState(DEFAULT_AUTO_GAMES);
  const [autoPayGames, setAutoPayGames] = useState(DEFAULT_AUTO_PAY_GAMES);
  const [concurrency, setConcurrency] = useState(2);
  const [savingSetting, setSavingSetting] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [totalPage, setTotalPage] = useState(0);
  const [page, setPage] = useState(0);
  const pageRef = useRef(page);

  const [newPeriodId, setNewPeriodId] = useState("");
  const [creating, setCreating] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  useEffect(() => {
    document.title = "Job tính thưởng";
  }, []);

  const fetchSetting = useCallback(async () => {
    const config = await getRewardCalcSetting(token);
    if (config) {
      setAutoGames({ ...DEFAULT_AUTO_GAMES, ...config.autoGames });
      setAutoPayGames({
        ...DEFAULT_AUTO_PAY_GAMES,
        ...config.autoPayGames,
      });
      setConcurrency(config.concurrency);
    }
  }, [token]);

  const fetchJobs = useCallback(async () => {
    const response = await getRewardCalcJobs(
      token,
      gameId,
      pageRef.current,
      period
    );
    if (response) {
      setJobs(response.data || []);
      setTotalPage(Math.ceil((response.total || 0) / JOBS_PER_PAGE));
    }
  }, [token, gameId, period]);

  useEffect(() => {
    fetchSetting();
  }, [fetchSetting]);

  useEffect(() => {
    const query = parseQuerySimple(location.search);
    const nextGameId = GAME_OPTIONS.some((game) => game.id === query.gameId)
      ? query.gameId
      : GAME_OPTIONS[0].id;
    const nextPeriod = query.period || "";

    setGameId(nextGameId);
    setPeriod(nextPeriod);
    setPeriodInput(nextPeriod);
    setPage(0);
  }, [location.search]);

  useEffect(() => {
    pageRef.current = page;
    fetchJobs();

    const intervalId = setInterval(fetchJobs, JOB_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [page, fetchJobs]);

  const updateFilterUrl = (nextGameId, nextPeriod) => {
    const searchParams = new URLSearchParams();
    searchParams.set("gameId", nextGameId);
    if (nextPeriod) {
      searchParams.set("period", nextPeriod);
    }
    history.push(`${location.pathname}?${searchParams.toString()}`);
  };

  const onChangeGame = (nextGameId) => {
    updateFilterUrl(nextGameId, "");
  };

  const onSearchPeriod = () => {
    updateFilterUrl(gameId, periodInput.trim());
  };

  const onClearPeriod = () => {
    updateFilterUrl(gameId, "");
  };

  const onSaveConcurrency = async () => {
    setSavingSetting(true);
    try {
      const config = await updateRewardCalcSetting(
        token,
        autoGames,
        autoPayGames,
        Number(concurrency)
      );
      if (config) {
        setAutoGames({ ...DEFAULT_AUTO_GAMES, ...config.autoGames });
        setAutoPayGames({
          ...DEFAULT_AUTO_PAY_GAMES,
          ...config.autoPayGames,
        });
        setConcurrency(config.concurrency);
        toast.success("Đã lưu cấu hình tính thưởng");
      } else {
        toast.error("Không thể lưu cấu hình tính thưởng");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể lưu cấu hình tính thưởng"
      );
    } finally {
      setSavingSetting(false);
    }
  };

  const onCreateJob = async () => {
    const gamePeriodId = newPeriodId.trim();
    if (!gamePeriodId) {
      toast.error("Vui lòng nhập mã kỳ quay");
      return;
    }

    setCreating(true);
    try {
      const response = await createRewardCalcJob(token, gameId, gamePeriodId);
      if (response && response.status === 1) {
        toast.success(`Đã tạo job tính thưởng cho kỳ ${gamePeriodId}`);
        setNewPeriodId("");
        fetchJobs();
      } else {
        toast.error(response?.message || "Không thể tạo job tính thưởng");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể tạo job tính thưởng"
      );
    } finally {
      setCreating(false);
    }
  };

  const onRetryJob = async (job) => {
    setRetryingId(job.id);
    try {
      const response = await retryRewardCalcJob(token, job.id);
      if (response && response.status === 1) {
        toast.success(`Đã đưa job #${job.id} vào hàng chờ tính lại`);
        fetchJobs();
      } else {
        toast.error(response?.message || "Không thể retry job");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Không thể retry job");
    } finally {
      setRetryingId(null);
    }
  };

  const renderStatusBadge = (status) => {
    const label = JOB_STATUS_LABELS[status] || {
      text: `Không rõ (${status})`,
      variant: "secondary",
    };
    return <Badge bg={label.variant}>{label.text}</Badge>;
  };

  return (
    <Card border="light" className="shadow-sm mb-4">
      <Card.Header>
        <h5 className="mb-0">Job tính thưởng</h5>
        <small className="text-muted">
          Theo dõi job tính thưởng, tạo job thủ công theo kỳ quay. Bật/tắt tính
          thưởng tự động từng game tại trang Tự động tính thưởng.
        </small>
      </Card.Header>
      <Card.Body>
        <Row className="mb-4 align-items-end">
          <Col md={3} className="mb-2 mb-md-0">
            <Form.Label className="small mb-1">Lọc theo game</Form.Label>
            <Form.Select
              size="sm"
              value={gameId}
              onChange={(event) => onChangeGame(event.target.value)}
            >
              {GAME_OPTIONS.map((game) => (
                <option key={game.id} value={game.id}>
                  {game.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6} className="mb-2 mb-md-0">
            <Form.Label className="small mb-1">Tìm theo kỳ</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                placeholder="Nhập tên kỳ hoặc mã kỳ..."
                value={periodInput}
                onChange={(event) => setPeriodInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    onSearchPeriod();
                  }
                }}
              />
              <Button variant="primary" onClick={onSearchPeriod}>
                Tìm kiếm
              </Button>
              {period && (
                <Button variant="outline-secondary" onClick={onClearPeriod}>
                  Xóa lọc
                </Button>
              )}
            </InputGroup>
          </Col>
          <Col md={3} className="d-flex justify-content-md-end">
            <Button variant="outline-secondary" size="sm" onClick={fetchJobs}>
              Tải lại danh sách
            </Button>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={4} className="mb-2 mb-md-0">
            <Form.Label className="small mb-1">
              Số job chạy đồng thời (tất cả game)
            </Form.Label>
            <InputGroup size="sm">
              <Form.Control
                type="number"
                min={1}
                max={10}
                value={concurrency}
                onChange={(event) => setConcurrency(event.target.value)}
                disabled={savingSetting}
              />
              <Button
                variant="outline-primary"
                onClick={onSaveConcurrency}
                disabled={savingSetting}
              >
                {savingSetting ? <Spinner animation="border" size="sm" /> : "Lưu"}
              </Button>
            </InputGroup>
          </Col>
          <Col md={6} className="mb-2 mb-md-0">
            <Form.Label className="small mb-1">Tạo job theo kỳ quay</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                placeholder={
                  CLASSIC_FAMILY_GAME_IDS.includes(gameId)
                    ? "Mã kết quả, ví dụ MB20072026"
                    : `Mã kỳ quay, ví dụ ${gameId}-0123456`
                }
                value={newPeriodId}
                onChange={(event) => setNewPeriodId(event.target.value)}
                disabled={creating}
              />
              <Button
                variant="outline-success"
                onClick={onCreateJob}
                disabled={creating}
              >
                {creating ? <Spinner animation="border" size="sm" /> : "Tạo job"}
              </Button>
            </InputGroup>
          </Col>
        </Row>

        <Table responsive size="sm" className="align-items-center table-flush">
          <thead className="thead-light">
            <tr>
              <th scope="col">#</th>
              <th scope="col">Kỳ quay</th>
              <th scope="col">Trạng thái</th>
              <th scope="col">Vé đã dò</th>
              <th scope="col">Trúng / Không trúng</th>
              <th scope="col">Tổng thưởng</th>
              <th scope="col">Lần chạy</th>
              <th scope="col">Lỗi</th>
              <th scope="col">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-muted">
                  Chưa có job tính thưởng nào
                </td>
              </tr>
            )}
            {jobs.map((job) => (
              <tr key={`reward-calc-job-${job.id}`}>
                <td>{job.id}</td>
                <td>
                  {job.gamePeriodName ? `#${job.gamePeriodName}` : ""}
                  <div className="small text-muted">{job.gamePeriodId}</div>
                </td>
                <td>{renderStatusBadge(job.status)}</td>
                <td>{job.processedTickets}</td>
                <td>
                  {job.rewardedTickets} / {job.unrewardedTickets}
                </td>
                <td>{Number(job.totalReward).toLocaleString("vi-VN")}</td>
                <td>{job.attempts}</td>
                <td className="small text-danger">{job.error}</td>
                <td>
                  {(job.status === JOB_STATUS.FAILED ||
                    job.status === JOB_STATUS.DONE) && (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => onRetryJob(job)}
                      disabled={retryingId === job.id}
                    >
                      {retryingId === job.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Tính lại"
                      )}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Pagination
          totalPage={totalPage}
          currentPage={page}
          onPageChange={(nextPage) => setPage(nextPage)}
        />
      </Card.Body>
    </Card>
  );
};
